/** Studio Mail identity helpers. Domain is configurable; never hardcode in UI. */

export const DEFAULT_MAIL_DOMAIN = "yatishara.com";

export const RESERVED_LOCAL_PARTS = new Set([
  "abuse",
  "admin",
  "administrator",
  "andre",
  "billing",
  "dallas",
  "development",
  "dev",
  "hello",
  "hostmaster",
  "johndoe",
  "mail",
  "mailer-daemon",
  "marketing",
  "no-reply",
  "noreply",
  "postmaster",
  "root",
  "shara",
  "sharaanddallas",
  "studio",
  "support",
  "team",
  "webmaster",
  "www",
]);

const LOCAL_PART_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;

export function defaultMailDomain(): string {
  const fromEnv = String(process.env.STUDIO_MAIL_DOMAIN ?? "").trim().toLowerCase();
  return fromEnv || DEFAULT_MAIL_DOMAIN;
}

export function normalizeEmail(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  const match = raw.match(/<([^>]+)>/);
  return String(match?.[1] ?? raw).trim().toLowerCase();
}

export function parseEmailParty(value: string | undefined | null): {
  address: string;
  name?: string;
} | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const angled = raw.match(/^(.*)<([^>]+)>\s*$/);
  if (angled) {
    const address = normalizeEmail(angled[2]);
    const name = angled[1].trim().replace(/^["']|["']$/g, "");
    if (!address || !address.includes("@")) return null;
    return name ? { address, name } : { address };
  }
  const address = normalizeEmail(raw);
  if (!address || !address.includes("@")) return null;
  return { address };
}

export function normalizeEmailList(
  value: string | string[] | undefined | null,
): { address: string; name?: string }[] {
  const items = Array.isArray(value)
    ? value
    : String(value ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
  const out: { address: string; name?: string }[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const party = typeof item === "string" ? parseEmailParty(item) : parseEmailParty(
      item && typeof item === "object" && "address" in item
        ? `${(item as { name?: string }).name ?? ""} <${(item as { address: string }).address}>`
        : String(item),
    );
    if (!party || seen.has(party.address)) continue;
    seen.add(party.address);
    out.push(party);
  }
  return out;
}

export function splitAddress(normalized: string): {
  localPart: string;
  domain: string;
} | null {
  const address = normalizeEmail(normalized);
  const at = address.lastIndexOf("@");
  if (at <= 0 || at === address.length - 1) return null;
  return {
    localPart: address.slice(0, at),
    domain: address.slice(at + 1),
  };
}

export function localPartFromUsername(username: string): string {
  return String(username ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, 64);
}

const SIGNUP_HANDLE_MAX = 30;

function signupChars(raw: string): string {
  return String(raw ?? "")
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\s_-]+/g, ".")
    .replace(/[^a-z0-9.]+/g, "");
}

function collapseSignupDots(value: string): string {
  return value.replace(/\.{2,}/g, ".").replace(/^\.+|\.+$/g, "");
}

/** Sign-up mailbox: `José García` → `jose.garcia`. Letters, digits, and dots only. */
export function usernameSlug(firstName: string, lastName: string): string {
  const first = collapseSignupDots(signupChars(firstName));
  const last = collapseSignupDots(signupChars(lastName));
  const joined = [first, last].filter(Boolean).join(".");
  return collapseSignupDots(joined).slice(0, SIGNUP_HANDLE_MAX).replace(/\.+$/g, "");
}

/** Field text while typing. Strips a pasted @yatishara.com and keeps a trailing dot. */
export function signupHandleDraft(raw: string): string {
  const stripped = String(raw ?? "")
    .replace(/@yatishara\.com\s*$/i, "")
    .replace(/@.*$/, "");
  return signupChars(stripped).replace(/\.{2,}/g, ".").replace(/^\.+/g, "").slice(0, SIGNUP_HANDLE_MAX);
}

/** Change-field handle ready to store: same slug, max length, no trailing dot. */
export function normalizeSignupHandle(raw: string): string {
  return signupHandleDraft(raw).replace(/\.+$/g, "");
}

/** `base`, then `base2`, `base3`, trimmed so the result still fits a username. */
export function signupHandleCandidate(base: string, n: number): string {
  const suffix = n > 1 ? String(n) : "";
  const root = base.slice(0, Math.max(0, SIGNUP_HANDLE_MAX - suffix.length)).replace(/\.+$/g, "");
  if (!root) return "";
  return `${root}${suffix}`;
}

/** Signup mailbox from a person's name: `Dallas Ferdinand` → `dallasferdinand`. */
export function localPartFromDisplayName(firstName: string, lastName: string): string {
  const first = localPartFromUsername(firstName);
  const last = localPartFromUsername(lastName);
  if (first && last) return localPartFromUsername(`${first}${last}`);
  return first || last;
}

export function isValidLocalPart(localPart: string): boolean {
  const value = String(localPart ?? "").trim().toLowerCase();
  if (!value || value.length > 64) return false;
  if (value.includes("..")) return false;
  return LOCAL_PART_RE.test(value);
}

export function isReservedLocalPart(localPart: string): boolean {
  return RESERVED_LOCAL_PARTS.has(String(localPart ?? "").trim().toLowerCase());
}

/** Shared ops inboxes admins may open and send as. */
export const ADMIN_OPS_LOCAL_PARTS = [
  "hello",
  "team",
  "billing",
  "marketing",
  "development",
  "dev",
  "andre",
] as const;

/** Staff personal inboxes admins may open and send as. */
export const ADMIN_STAFF_LOCAL_PARTS = ["dallas", "shara", "sharaanddallas"] as const;

/**
 * Assistant inboxes (each its own Studio user) that platform admins also see in
 * their mailbox list, so the assistant's sign-in codes can be read from there.
 * Unlike staff inboxes, these addresses stay on their own account.
 */
export const ADMIN_AGENT_LOCAL_PARTS = ["nova.edits"] as const;

export function isAdminMailboxLocalPart(localPart: string): boolean {
  const local = String(localPart ?? "").trim().toLowerCase();
  return (
    (ADMIN_OPS_LOCAL_PARTS as readonly string[]).includes(local) ||
    (ADMIN_STAFF_LOCAL_PARTS as readonly string[]).includes(local) ||
    (ADMIN_AGENT_LOCAL_PARTS as readonly string[]).includes(local)
  );
}

export function formatAddress(localPart: string, domain: string): string {
  return `${String(localPart).trim().toLowerCase()}@${String(domain).trim().toLowerCase()}`;
}

/** Sign-in typing: `you` or `you@yatishara.com`. External addresses are not logins. */
export function parseLoginIdentifier(raw: string): {
  email: string;
  localPart: string | null;
  kind: "handle" | "yatishara" | "external";
} | null {
  const value = String(raw ?? "").trim().toLowerCase();
  if (!value) return null;
  const domain = defaultMailDomain();
  if (!value.includes("@")) {
    const localPart = localPartFromUsername(value);
    if (!localPart) return null;
    return {
      email: formatAddress(localPart, domain),
      localPart,
      kind: "handle",
    };
  }
  const parts = splitAddress(value);
  if (!parts) return null;
  if (parts.domain === domain) {
    return {
      email: formatAddress(parts.localPart, domain),
      localPart: parts.localPart,
      kind: "yatishara",
    };
  }
  return { email: value, localPart: null, kind: "external" };
}

export function participantKey(
  parties: Array<{ address?: string } | string | null | undefined>,
): string {
  const set = new Set<string>();
  for (const party of parties) {
    const address =
      typeof party === "string" ? normalizeEmail(party) : normalizeEmail(party?.address);
    if (address) set.add(address);
  }
  return [...set].sort().join("|");
}
